import http from "@services/httpService"
import type { PagedResultDto } from "@services/dto/pagedResultDto"
import type {
  GetMyNotificationsInput,
  GetSettingsInput,
  NotificationDefinitionDto,
  NotificationDefinitionFilter,
  NotificationMessageDto,
  NotificationSettingMatrixDto,
  UpdateNotificationSettingInput,
} from "@services/notification/dto/notificationCenterDto"

/**
 * Client REST cho Notification Center, bọc `httpService` (axios) theo URL ABP
 * `/api/services/app/NotificationCenter/{Method}` và envelope `{ result, success }`.
 *
 * Quy ước ABP dynamic Web API: method tiền tố `Get*` → HTTP GET (params trên query),
 * các method còn lại → HTTP POST (body). Tham chiếu: Requirements 6.1, 6.3, 6.4.
 */
class NotificationCenterService {
  private readonly baseUrl = "api/services/app/NotificationCenter"

  /** Danh sách thông báo của người dùng hiện tại: loại hết hạn, sort desc, filter (R6.3/6.4/2.10). */
  public async getMyNotifications(
    input: GetMyNotificationsInput
  ): Promise<PagedResultDto<NotificationMessageDto>> {
    const res = await http.get(`${this.baseUrl}/GetMyNotifications`, {
      params: input,
    })
    return res.data.result
  }

  /** Số thông báo chưa đọc còn hiệu lực của người dùng hiện tại (R6.1). */
  public async getUnreadCount(): Promise<number> {
    const res = await http.get(`${this.baseUrl}/GetUnreadCount`)
    return res.data.result
  }

  /** Đánh dấu một thông báo là đã đọc (R6.5). */
  public async markAsRead(id: string): Promise<void> {
    await http.post(`${this.baseUrl}/MarkAsRead`, { id })
  }

  /** Đánh dấu tất cả thông báo chưa đọc của người dùng hiện tại là đã đọc (R6.6). */
  public async markAllAsRead(): Promise<void> {
    await http.post(`${this.baseUrl}/MarkAllAsRead`)
  }

  /** Ma trận cấu hình (Event × Channel) ở một cấp/scope, gồm cờ MandatoryLocked (R6.7/6.8). */
  public async getSettings(
    input: GetSettingsInput
  ): Promise<NotificationSettingMatrixDto> {
    const res = await http.get(`${this.baseUrl}/GetSettings`, { params: input })
    return res.data.result
  }

  /** Cập nhật một ô cấu hình ở một cấp/scope (R4.8/4.10/6.8). */
  public async updateSetting(
    input: UpdateNotificationSettingInput
  ): Promise<void> {
    await http.post(`${this.baseUrl}/UpdateSetting`, input)
  }

  /** Truy vấn danh mục Notification_Definition đã đăng ký, có lọc (R1.7). */
  public async queryRegistry(
    filter: NotificationDefinitionFilter
  ): Promise<PagedResultDto<NotificationDefinitionDto>> {
    const res = await http.post(`${this.baseUrl}/QueryRegistry`, filter)
    return res.data.result
  }
}

export default new NotificationCenterService()
